import React, { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Badge } from "./ui/badge";
import useFirebaseGlassData from "../hooks/useFirebaseGlassData";
import useFirebaseInventory from "../hooks/useFirebaseInventory";
import {
  getHexColor,
  getGlassReactionTypeCharacters,
  getGlassReactionTypeColour,
} from "../helpers/glassHelpers";
import {
  padPrefix,
  findGlassInventoryEntry,
  getPositionIndices,
} from "../helpers/colourWheelHelpers";
import type { ColourScheme } from "../helpers/colourWheelHelpers";
import colourWheelData from "../helpers/bullseye_colour_wheel.json";
import type { GlassData } from "../types";

const COLOUR_NAMES = [
  "yellow",
  "yellow-green",
  "green",
  "blue-green",
  "blue",
  "blue-violet",
  "violet",
  "red-violet",
  "red",
  "red-orange",
  "orange",
  "yellow-orange",
] as const;

type ColourName = (typeof COLOUR_NAMES)[number];

const COLOUR_DISPLAY: Record<ColourName, { label: string; hue: string }> = {
  yellow: { label: "Yellow", hue: "#eab308" },
  "yellow-green": { label: "Yellow-Green", hue: "#84cc16" },
  green: { label: "Green", hue: "#22c55e" },
  "blue-green": { label: "Blue-Green", hue: "#14b8a6" },
  blue: { label: "Blue", hue: "#3b82f6" },
  "blue-violet": { label: "Blue-Violet", hue: "#6366f1" },
  violet: { label: "Violet", hue: "#8b5cf6" },
  "red-violet": { label: "Red-Violet", hue: "#d946ef" },
  red: { label: "Red", hue: "#ef4444" },
  "red-orange": { label: "Red-Orange", hue: "#f97316" },
  orange: { label: "Orange", hue: "#fb923c" },
  "yellow-orange": { label: "Yellow-Orange", hue: "#f59e0b" },
};

const REACTION_FULL_NAME: Record<string, string> = {
  Cu: "Copper",
  S: "Sulfur/Selenium",
  Cd: "Cadmium",
  Pb: "Lead",
  RE: "Reactive",
};

type GlassType = "opalescent" | "transparent";

type SchemeKey = "single" | "complement" | "split" | "triadic" | "tetradic";

const SCHEMES: Record<SchemeKey, { label: string; scheme: ColourScheme | null; roles: string[] }> = {
  single: { label: "Single", scheme: null, roles: ["Base"] },
  complement: {
    label: "Complement",
    scheme: { label: "Complement", offsets: [6] },
    roles: ["Base", "Complement"],
  },
  split: {
    label: "Split",
    scheme: { label: "Split", offsets: [5, 7] },
    roles: ["Base", "Split L", "Split R"],
  },
  triadic: {
    label: "Triadic",
    scheme: { label: "Triadic", offsets: [4, 8] },
    roles: ["Base", "Triad A", "Triad B"],
  },
  tetradic: {
    label: "Tetradic",
    scheme: { label: "Tetradic", offsets: [4, 6, 10] },
    roles: ["Base", "Tetrad A", "Tetrad B", "Tetrad C"],
  },
};

interface ColourWheelEntry {
  prefix: string;
  luminance: number;
}

const shortLabel = (name: ColourName): string => {
  const parts = name.split("-");
  if (parts.length === 1) return parts[0][0].toUpperCase() + parts[0].slice(1, 3);
  return parts.map((s) => s[0].toUpperCase() + s.slice(1, 3)).join("-");
};

const isLightColour = (hex: string): boolean => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return false;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return (r * 299 + g * 587 + b * 114) / 1000 > 140;
};

const ColourWheel = ({
  selected,
  activeIndices,
  onSelect,
}: {
  selected: number;
  activeIndices: number[];
  onSelect: (index: number) => void;
}) => {
  const R = 180;
  const r = 65;
  const segAngle = (2 * Math.PI) / 12;

  const segments = Array.from({ length: 12 }, (_, i) => {
    const a0 = -Math.PI / 2 + i * segAngle - segAngle / 2;
    const a1 = a0 + segAngle;
    const x0 = R * Math.cos(a0);
    const y0 = R * Math.sin(a0);
    const x1 = R * Math.cos(a1);
    const y1 = R * Math.sin(a1);
    const xi0 = r * Math.cos(a0);
    const yi0 = r * Math.sin(a0);
    const xi1 = r * Math.cos(a1);
    const yi1 = r * Math.sin(a1);
    const aMid = a0 + segAngle / 2;
    const rLabel = (R + r) / 2;
    const lx = rLabel * Math.cos(aMid);
    const ly = rLabel * Math.sin(aMid);
    const name = COLOUR_NAMES[i];
    const isActive = activeIndices.includes(i);
    const isBase = i === selected;
    const d = `M ${x0} ${y0} A ${R} ${R} 0 0 1 ${x1} ${y1} L ${xi1} ${yi1} A ${r} ${r} 0 0 0 ${xi0} ${yi0} Z`;
    return { i, name, d, lx, ly, isActive, isBase };
  });

  return (
    <svg
      viewBox="-200 -200 400 400"
      className="w-full h-auto block"
      style={{ maxWidth: 380 }}
    >
      {segments.map((seg) => (
        <path
          key={seg.i}
          d={seg.d}
          fill={COLOUR_DISPLAY[seg.name].hue}
          stroke="#fff"
          strokeWidth={2}
          onClick={() => onSelect(seg.i)}
          style={{
            cursor: "pointer",
            opacity: seg.isActive ? 1 : 0.25,
            filter: seg.isBase ? "drop-shadow(0 0 6px rgba(0,0,0,0.35))" : undefined,
            transition: "opacity .15s, filter .15s",
          }}
        />
      ))}
      {segments.map((seg) => (
        <text
          key={`label-${seg.i}`}
          x={seg.lx}
          y={seg.ly}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#fff"
          fontSize={11}
          fontWeight={600}
          style={{ pointerEvents: "none", userSelect: "none" }}
        >
          {shortLabel(seg.name)}
        </text>
      ))}
    </svg>
  );
};

const ColourPanel = ({
  colourName,
  role,
  entries,
  glassData,
  onSelectGlass,
  isBase,
}: {
  colourName: ColourName;
  role: string;
  entries: ColourWheelEntry[];
  glassData: GlassData | null | undefined;
  onSelectGlass: (prefix: string) => void;
  isBase: boolean;
}) => {
  const sorted = [...entries].sort((a, b) => a.luminance - b.luminance);
  const display = COLOUR_DISPLAY[colourName];

  return (
    <div
      className={`rounded-xl border p-3.5 bg-card transition-all ${
        isBase ? "border-primary ring-[3px] ring-primary/20" : "border-border"
      }`}
    >
      <h3 className="flex items-center gap-2 mb-2.5 text-sm font-semibold">
        <span
          className="inline-block h-3 w-3 rounded-sm"
          style={{ backgroundColor: display.hue }}
        />
        {display.label}
        <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded">
          {role}
        </span>
      </h3>
      {sorted.length === 0 ? (
        <div className="text-xs text-muted-foreground py-1">No codes in this segment.</div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {sorted.map((entry) => {
            const padded = padPrefix(entry.prefix);
            const inv = findGlassInventoryEntry(entry.prefix, glassData);
            const rgb = inv?.color?.rgb;
            const bgColor = rgb ? getHexColor(rgb) : display.hue;
            const light = isLightColour(bgColor);
            const reactionChars = glassData
              ? getGlassReactionTypeCharacters(padded, glassData)
              : "";

            return (
              <button
                key={entry.prefix}
                className="relative min-w-[64px] rounded-lg px-3 py-2.5 font-mono text-[13px] font-semibold border-0 cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md"
                style={{
                  backgroundColor: bgColor,
                  color: light ? "#000" : "#fff",
                }}
                onClick={() => onSelectGlass(entry.prefix)}
                title={inv?.description ?? entry.prefix}
              >
                {entry.prefix}
                {reactionChars && (
                  <span
                    className="absolute -top-1.5 -right-1.5 inline-flex h-[18px] w-[18px] items-center justify-center rounded-full text-[9px] font-bold text-white border-[1.5px] border-white"
                    style={{
                      backgroundColor: glassData
                        ? getGlassReactionTypeColour(padded, glassData)
                        : "#888",
                    }}
                  >
                    {reactionChars}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const GlassDetailDialog = ({
  prefix,
  glassType,
  glassData,
  inventoryMatches,
  onClose,
}: {
  prefix: string | null;
  glassType: GlassType;
  glassData: GlassData | null | undefined;
  inventoryMatches: { id: string; count?: number; type?: string }[];
  onClose: () => void;
}) => {
  if (!prefix) return null;

  const padded = padPrefix(prefix);
  const inv = findGlassInventoryEntry(prefix, glassData);
  const rgb = inv?.color?.rgb;
  const bgColor = rgb ? getHexColor(rgb) : "#888";
  const reactionChars = glassData ? getGlassReactionTypeCharacters(padded, glassData) : "";
  const reactionColour = glassData ? getGlassReactionTypeColour(padded, glassData) : "#888";
  const reactionName = reactionChars ? REACTION_FULL_NAME[reactionChars] : undefined;

  return (
    <Dialog open={!!prefix} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <span
              className="h-[22px] w-[22px] rounded border border-border shrink-0"
              style={{ backgroundColor: bgColor }}
            />
            <span>
              {prefix}
              {inv?.description && ` — ${inv.description}`}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div
            className="h-[72px] w-full rounded-lg border border-border"
            style={{ backgroundColor: bgColor }}
          />

          <div className="flex flex-wrap gap-1.5">
            {reactionChars && (
              <span
                className="text-[11px] font-medium px-2.5 py-0.5 rounded-full text-white"
                style={{ backgroundColor: reactionColour }}
              >
                {reactionChars}
                {reactionName && ` · ${reactionName}`}
              </span>
            )}
            <Badge variant="secondary" className="text-[11px] font-medium">
              Bullseye · {glassType === "opalescent" ? "Opalescent" : "Transparent"}
            </Badge>
            {inv?.price_band && (
              <Badge variant="secondary" className="text-[11px] font-medium">
                Price band: {inv.price_band}
              </Badge>
            )}
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
              {inventoryMatches.length > 0 ? "In your inventory" : "Inventory"}
            </h4>
            {inventoryMatches.length > 0 ? (
              <ul className="space-y-1">
                {inventoryMatches.map((item) => (
                  <li
                    key={item.id}
                    className="flex justify-between px-2.5 py-1.5 rounded bg-muted font-mono text-xs"
                  >
                    <span>{item.type ?? prefix}</span>
                    <span>× {item.count ?? 1}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground">Not in your inventory.</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const SegmentedToggle = <T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) => (
  <div className="flex flex-col gap-1.5">
    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      {label}
    </span>
    <div className="inline-flex rounded-lg border border-border bg-card overflow-hidden">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`h-[34px] px-3.5 text-[13px] font-medium transition-colors border-0 cursor-pointer ${
            value === opt.value
              ? "bg-primary text-primary-foreground"
              : "bg-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  </div>
);

const ColourWheelPage = () => {
  const [glassType, setGlassType] = useState<GlassType>("opalescent");
  const [selectedPosition, setSelectedPosition] = useState(0);
  const [schemeKey, setSchemeKey] = useState<SchemeKey>("single");
  const [detailPrefix, setDetailPrefix] = useState<string | null>(null);

  const glassData = useFirebaseGlassData("Bullseye");
  const inventory = useFirebaseInventory();

  const wheelData = colourWheelData[glassType] as Record<ColourName, ColourWheelEntry[]>;
  const scheme = SCHEMES[schemeKey];

  const activeIndices = useMemo(
    () => getPositionIndices(selectedPosition, scheme.scheme),
    [selectedPosition, scheme.scheme]
  );

  const inventoryMatches = useMemo(() => {
    if (!detailPrefix || !inventory) return [];
    const padded = padPrefix(detailPrefix);
    return Object.entries(inventory)
      .filter(
        ([, item]) =>
          item.glass_reference?.startsWith(padded) &&
          (!item.glass_type || item.glass_type === "Bullseye")
      )
      .map(([id, item]) => ({
        id,
        count: item.count,
        type: item.glass_reference
          ? glassData?.inventory?.[item.glass_reference]?.type
          : undefined,
      }));
  }, [detailPrefix, inventory, glassData]);

  const selectedName = COLOUR_NAMES[selectedPosition];

  return (
    <div>
      <div className="mb-2">
        <h1 className="text-2xl font-bold tracking-tight">
          Colour Wheel
          <span className="ml-1.5 text-[11px] font-medium text-muted-foreground align-super">
            Premium
          </span>
        </h1>
      </div>
      <p className="max-w-[680px] mb-5 text-[13px] leading-relaxed text-muted-foreground">
        Interactive Colour Wheel for Bullseye Glass. Choose Opalescent or Transparent, pick a hue
        on the wheel, then try a Complement, Split, Triadic or Tetradic scheme to find harmonious
        combinations. Click any glass code to see its reaction type and inventory status.
      </p>

      <div className="flex flex-wrap items-start gap-5 mb-5">
        <SegmentedToggle
          label="Glass type"
          value={glassType}
          onChange={setGlassType}
          options={[
            { value: "opalescent", label: "Opalescent" },
            { value: "transparent", label: "Transparent" },
          ]}
        />
        <SegmentedToggle
          label="Scheme"
          value={schemeKey}
          onChange={setSchemeKey}
          options={(["single", "complement", "split", "triadic", "tetradic"] as SchemeKey[]).map(
            (k) => ({ value: k, label: SCHEMES[k].label })
          )}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[380px_1fr] gap-8 items-start">
        <div className="relative">
          <ColourWheel
            selected={selectedPosition}
            activeIndices={activeIndices}
            onSelect={setSelectedPosition}
          />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[110px] h-[110px] rounded-full bg-card border border-border shadow-sm flex flex-col items-center justify-center text-center pointer-events-none">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Selected
            </div>
            <div className="text-sm font-semibold mt-0.5">
              {COLOUR_DISPLAY[selectedName].label}
            </div>
            <div className="text-[11px] font-medium text-primary mt-1">{scheme.label}</div>
          </div>
        </div>

        <div className="flex flex-col gap-3 min-w-0">
          {activeIndices.map((idx, ord) => {
            const colourName = COLOUR_NAMES[idx];
            return (
              <ColourPanel
                key={`${colourName}-${ord}`}
                colourName={colourName}
                role={scheme.roles[ord] ?? ""}
                entries={wheelData[colourName] ?? []}
                glassData={glassData}
                onSelectGlass={setDetailPrefix}
                isBase={ord === 0}
              />
            );
          })}
        </div>
      </div>

      <GlassDetailDialog
        prefix={detailPrefix}
        glassType={glassType}
        glassData={glassData}
        inventoryMatches={inventoryMatches}
        onClose={() => setDetailPrefix(null)}
      />
    </div>
  );
};

export default ColourWheelPage;
